"use strict";
/**
 * 模板层：一套主题怎么把一个页面拼出来。
 *
 * 这是「主题 = 页面模板」这句话在代码里的落点。令牌层只能改颜色字号，
 * 原型（archetype）是三个封闭枚举，两者都表达不了 hubs.com 那种
 * 「顶栏 + 深色页头 + 左图右文首屏 + 能力卡四栏 + 伙伴卡片横滑 + 页脚五列」。
 * 差的那个位置就是这张表：**哪个区域、放哪些组件、什么顺序**。
 *
 * 三条纪律：
 *
 *   1) **组件只能来自平台注册表。** 主题里写的是一个组件名，不是一个实现。
 *      第三方主题因此不可能带代码进来，校验器也能在发布前把它全部核对完。
 *   2) **实例有稳定 id。** 客户改过的文案按 id 存成 patch，主题升级时
 *      只补新增的实例、不覆盖客户改过的内容 —— 没有稳定 id，升级就等于重置。
 *   3) **区域是封闭枚举。** 五个位置覆盖了 90% 的企业站：工具条、页头、
 *      首屏、主体、页脚。放开「任意区域」会让每个主题发明自己的位置，
 *      主题之间的组件就再也换不了。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SHARED_REGIONS = exports.SECTION_TONES = exports.PAGE_KIND_FALLBACK = exports.PAGE_KIND_LABEL = exports.THEME_PAGE_KINDS = exports.REGION_LABEL = exports.THEME_REGIONS = void 0;
exports.isSharedRegion = isSharedRegion;
exports.isThemeRegion = isThemeRegion;
exports.isThemePageKind = isThemePageKind;
exports.resolvePageLayout = resolvePageLayout;
exports.layoutForPage = layoutForPage;
exports.hasLayout = hasLayout;
exports.eachLayoutItem = eachLayoutItem;
exports.styleVarName = styleVarName;
exports.componentStyleVars = componentStyleVars;
exports.validateLayoutShape = validateLayoutShape;
/** 页面上可以放组件的五个位置。顺序即渲染顺序 */
exports.THEME_REGIONS = ['utility', 'header', 'hero', 'main', 'footer'];
exports.REGION_LABEL = {
    utility: { zh: '工具条', en: 'Utility bar' },
    header: { zh: '页头', en: 'Header' },
    hero: { zh: '首屏', en: 'Hero' },
    main: { zh: '主体', en: 'Main' },
    footer: { zh: '页脚', en: 'Footer' },
};
/**
 * 页面类型。与 `PagePayload.page.kind` 不是一一对应：
 * 那边是「这是一篇什么内容」，这边是「用哪套模板」。
 * 商品列表与联系页在内容层都叫 `page`，但它们要的版式完全不同。
 */
exports.THEME_PAGE_KINDS = ['home', 'products', 'product', 'contact', 'page'];
exports.PAGE_KIND_LABEL = {
    home: { zh: '首页', en: 'Home' },
    products: { zh: '产品列表', en: 'Product list' },
    product: { zh: '产品详情', en: 'Product detail' },
    contact: { zh: '联系页', en: 'Contact' },
    page: { zh: '普通内容页', en: 'Content page' },
};
/**
 * 兜底链：这个页面类型没写版式时，退到哪一档。
 *
 * 为什么需要它：主题作者少写一种页面就会掉回老路径（硬编码页头 + 区块列表），
 * 视觉上像"换了个站"。有兜底之后，商品详情页至少能借用内容页的结构，
 * 外壳与主体节奏仍然是同一套主题。
 *
 * `home` 与 `page` 不退：
 *   - 首页是门户，形态独特，退到内容页只会更糟；
 *   - `page` 已经是链尾。
 *
 * 注意降级后组件限制仍按**实际页面类型**判（见 `resolvePageLayout` 的注释），
 * 不是按版式来源判 —— 否则只在内容页合法的组件会跑进商品页且不报错。
 */
exports.PAGE_KIND_FALLBACK = {
    home: null,
    products: 'page',
    product: 'page',
    contact: 'page',
    page: null,
};
/**
 * 区域底色。
 *
 * 为什么这是**版式**的事而不是组件的事：一条页面从上到下是"白 → 浅灰 → 白 →
 * 深色收口"，这个节奏属于整套版式，不属于其中任何一段。放在组件属性上，
 * 结果是同一个组件在五套主题里各长一个 `tone` 字段，而客户在控制台里能把它
 * 改成和邻居一样的底色，节奏一改就散。
 *
 * 四档覆盖了绝大多数企业站：`default` 用页面底色，`muted` 用浅灰面，
 * `dark` 用页头那套深色，`brand` 用主色满铺（只留给真正的转化段）。
 * 更多档位不是不能加，而是加一档就要在四套主题里各找一个用得上它的位置。
 */
exports.SECTION_TONES = ['default', 'muted', 'dark', 'brand'];
/** 共用外壳允许的区域 */
exports.SHARED_REGIONS = ['utility', 'header', 'footer'];
function isSharedRegion(region) {
    return exports.SHARED_REGIONS.includes(region);
}
function isThemeRegion(value) {
    return typeof value === 'string' && exports.THEME_REGIONS.includes(value);
}
function isThemePageKind(value) {
    return typeof value === 'string' && exports.THEME_PAGE_KINDS.includes(value);
}
/**
 * 共用外壳垫底、页面自己的同名区域覆盖。
 *
 * 用整个区域覆盖而不是"按组件合并"：合并会让客户永远删不掉共用页头里的
 * 某一项（它来自 shared），而"这个页面我想用另一套页头"是完全合理的需求。
 */
function mergeWithShared(layout, page) {
    const merged = { ...(layout?.shared ?? {}), ...(page ?? {}) };
    return Object.keys(merged).length > 0 ? merged : null;
}
/**
 * 这一档页面是否**真的写了**版式。
 *
 * 不能只判 `pages[kind] !== undefined`：`{}` 是合法的空对象，
 * 判成"写了"会直接跳过降级，最后渲染出一页只有外壳的空白 —— 而没有任何报错。
 */
function hasOwnPageLayout(layout, kind) {
    const page = layout?.pages?.[kind];
    return Boolean(page && Object.keys(page).length > 0);
}
/**
 * 按降级链解析一个页面该用哪套版式。
 *
 * 顺序：实体级 → 本类型 → 兜底类型 → 只剩外壳 → 老路径。
 *
 * `request.entityLayout` 由调用方解析好传进来（第二期接绑定表时，
 * 调用方先查库拿到那一套，再交给这里）。theme-kit 是纯包，不依赖数据库 ——
 * 它只负责"优先级"，不负责"数据从哪来"。
 */
function resolvePageLayout(layout, request) {
    const requestedKind = request.kind;
    if (request.entityLayout && Object.keys(request.entityLayout).length > 0) {
        const own = mergeWithShared(layout, request.entityLayout);
        if (own) {
            return { layout: own, requestedKind, matchedKind: requestedKind, matchedBy: 'entity' };
        }
    }
    if (hasOwnPageLayout(layout, requestedKind)) {
        return {
            layout: mergeWithShared(layout, layout?.pages?.[requestedKind]),
            requestedKind,
            matchedKind: requestedKind,
            matchedBy: 'kind',
        };
    }
    const fallbackKind = exports.PAGE_KIND_FALLBACK[requestedKind];
    if (fallbackKind && hasOwnPageLayout(layout, fallbackKind)) {
        return {
            layout: mergeWithShared(layout, layout?.pages?.[fallbackKind]),
            requestedKind,
            matchedKind: fallbackKind,
            matchedBy: 'fallback',
        };
    }
    // 没有页面版式，但主题声明了共用外壳：仍是有效渲染（老行为，不是错误）
    const shellOnly = mergeWithShared(layout, undefined);
    if (shellOnly) {
        return { layout: shellOnly, requestedKind, matchedKind: null, matchedBy: 'shell' };
    }
    return { layout: null, requestedKind, matchedKind: null, matchedBy: 'none' };
}
/** 该页面类型对应的区域表；主题没声明就是 null，调用方回落到老路径 */
function layoutForPage(layout, kind) {
    return resolvePageLayout(layout, { kind }).layout;
}
/** 主题是否提供了模板层。false 时全站走今天的老路径（硬编码页头 + 区块列表） */
function hasLayout(manifest) {
    const layout = manifest.layout;
    return Boolean(layout && (Object.keys(layout.pages ?? {}).length > 0 || (layout.shared && Object.keys(layout.shared).length > 0)));
}
/** 遍历模板里所有实例，按页面 + 区域 + 顺序展开 */
function eachLayoutItem(layout, visit) {
    for (const region of exports.SHARED_REGIONS) {
        for (const item of layout.shared?.[region] ?? [])
            visit(item, 'shared', region);
    }
    for (const page of exports.THEME_PAGE_KINDS) {
        const pageLayout = layout.pages[page];
        if (!pageLayout)
            continue;
        for (const region of exports.THEME_REGIONS) {
            for (const item of pageLayout[region] ?? [])
                visit(item, page, region);
        }
    }
}
/** 组件样式槽对应的 CSS 变量名：`--c-<组件>-<槽>`。组件只读这一层 */
function styleVarName(component, slot) {
    const kebab = (v) => v.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
    return `--c-${kebab(component)}-${kebab(slot)}`;
}
/**
 * 把主题声明的组件样式摊平成 CSS 变量。
 *
 * 与令牌层同一套命名规则，所以 `theme.componentStyles.header.height: 96`
 * 会变成 `--c-header-height: 96px` —— 但组件读的是**自己的**前缀，
 * 而不是 `--header-height`：两套主题给同一个组件配不同数量的槽时，
 * 全局命名迟早撞车，前缀把它变成不可能。
 *
 * 数字按 px 处理，除非键名以 `ratio` / `scale` / `index` / `z` 结尾
 * （与 resolve.ts 的 UNITLESS_RE 保持同一口径）。
 */
const UNITLESS = /(?:ratio|scale|index|z|opacity|weight|columns|lines|count)$/i;
function componentStyleVars(styles) {
    const vars = {};
    if (!styles)
        return vars;
    for (const [component, slots] of Object.entries(styles)) {
        for (const [slot, value] of Object.entries(slots ?? {})) {
            const name = styleVarName(component, slot);
            vars[name] = typeof value === 'number' && !UNITLESS.test(slot) ? `${value}px` : String(value);
        }
    }
    return vars;
}
/**
 * 结构校验（只判形状，不判组件是否存在）。
 *
 * 组件是否注册要问组件注册表，那是 `@icetus/blocks` 的事 —— theme-kit
 * 反过来依赖 blocks 会形成环（blocks 依赖 contracts，theme-kit 只依赖自己）。
 * 所以分两处：这里管"模板自己有没有毛病"，命令行与后台管"组件认不认识"。
 */
function validateLayoutShape(layout) {
    const issues = [];
    if (layout === null || typeof layout !== 'object') {
        return [{ message: 'layout 必须是对象' }];
    }
    const root = layout;
    if (root.version !== 1) {
        issues.push({ message: `layout.version 必须是 1，当前 ${String(root.version)}` });
    }
    const pages = root.pages;
    if (pages === null || typeof pages !== 'object') {
        return [...issues, { message: 'layout.pages 必须是对象' }];
    }
    /**
     * 实例 id 全站唯一，而不是单页唯一。
     * 客户的文案 patch 是一张按 id 的平表，重 id 会让两处显示同一段文字 ——
     * 症状是"我改了一处另一处跟着变"，在页面上几乎查不出来。
     */
    const seen = new Map();
    const checkRegion = (page, regionKey, items) => {
        if (!isThemeRegion(regionKey)) {
            issues.push({ page, message: `未知区域 ${regionKey}` });
            return;
        }
        if (!isSharedRegion(regionKey) && page === 'shared') {
            issues.push({ page, region: regionKey, message: `共用外壳不能放 ${regionKey} 区域（只允许 utility / header / footer）` });
            return;
        }
        if (!Array.isArray(items)) {
            issues.push({ page, region: regionKey, message: '区域的组件列表必须是数组' });
            return;
        }
        /*
         * 首屏只能有一个。
         *
         * 四种首屏组件各自都是单例，但"单例"是按**组件类型**算的 ——
         * 一个 `hero-split` 加一个 `hero-carousel` 各自都没超标，页面上却
         * 出现两个 `h1` 和两屏首图。这条限制只能落在区域上。
         *
         * 为什么值得占一条硬校验：主题包不走控制台保存校验（工坊导出直接
         * 上传），放错的东西能直达生产；而"两个首屏"在页面上看起来像
         * 设计失误，倒推起来要翻一整份 layout.json。
         */
        if (regionKey === 'hero' && items.length > 1) {
            issues.push({ page, region: regionKey, message: `首屏区域只能有一个组件，当前 ${items.length} 个` });
        }
        for (const raw of items) {
            if (raw === null || typeof raw !== 'object') {
                issues.push({ page, region: regionKey, message: '组件实例必须是对象' });
                continue;
            }
            const item = raw;
            if (typeof item.id !== 'string' || !/^[a-z0-9][a-z0-9-]{1,60}$/.test(item.id)) {
                issues.push({
                    page,
                    region: regionKey,
                    message: `实例 id 必须是小写字母/数字/连字符，2-61 位，当前 ${String(item.id)}`,
                });
                continue;
            }
            const owner = seen.get(item.id);
            if (owner) {
                issues.push({
                    page,
                    region: regionKey,
                    itemId: item.id,
                    message: `实例 id 重复（已出现在 ${owner}）`,
                });
            }
            else {
                seen.set(item.id, `${page}.${regionKey}`);
            }
            if (typeof item.component !== 'string' || !/^[a-z][a-z0-9-]*$/.test(item.component)) {
                issues.push({
                    page,
                    region: regionKey,
                    itemId: item.id,
                    message: `组件名必须是 kebab-case，当前 ${String(item.component)}`,
                });
            }
            if (item.props !== undefined && (item.props === null || typeof item.props !== 'object')) {
                issues.push({ page, region: regionKey, itemId: item.id, message: 'props 必须是对象' });
            }
            if (item.anchor !== undefined && !/^[a-z][a-z0-9-]*$/.test(String(item.anchor))) {
                issues.push({
                    page,
                    region: regionKey,
                    itemId: item.id,
                    message: `锚点必须是 kebab-case，当前 ${String(item.anchor)}`,
                });
            }
            /**
             * 底色只在 `main` 上生效。写错区域不报错也能渲染（首屏的 tone 由组件自己管），
             * 但作者会以为"我明明写了 muted" —— 这种沉默的失效最难查，所以拦下来。
             */
            if (item.tone !== undefined) {
                if (!exports.SECTION_TONES.includes(String(item.tone))) {
                    issues.push({
                        page,
                        region: regionKey,
                        itemId: item.id,
                        message: `底色只能是 ${exports.SECTION_TONES.join(' / ')}，当前 ${String(item.tone)}`,
                    });
                }
                else if (regionKey !== 'main') {
                    issues.push({
                        page,
                        region: regionKey,
                        itemId: item.id,
                        message: `底色只对主体区域的段生效，「${item.id}」在 ${regionKey} 区域，
              首屏与外壳的配色由组件自己决定`,
                    });
                }
            }
        }
    };
    const shared = root.shared;
    if (shared !== undefined) {
        if (shared === null || typeof shared !== 'object') {
            issues.push({ page: 'shared', message: 'layout.shared 必须是对象' });
        }
        else {
            for (const [regionKey, items] of Object.entries(shared)) {
                checkRegion('shared', regionKey, items);
            }
        }
    }
    for (const [pageKey, pageLayout] of Object.entries(pages)) {
        if (!isThemePageKind(pageKey)) {
            issues.push({ message: `未知页面类型 ${pageKey}` });
            continue;
        }
        if (pageLayout === null || typeof pageLayout !== 'object') {
            issues.push({ page: pageKey, message: '页面版式必须是对象' });
            continue;
        }
        for (const [regionKey, items] of Object.entries(pageLayout)) {
            checkRegion(pageKey, regionKey, items);
        }
    }
    return issues;
}
//# sourceMappingURL=layout.js.map